import React, { useCallback, useEffect, useState } from 'react';
import { Button, Modal, Space, Tooltip, Typography } from 'antd';
import { ImportOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SupplierOrderTruckFreightList from './SupplierOrderTruckFreightList';
import DeliveryTransportPicker from '../../transports/components/DeliveryTransportPicker';
import TransportAttachCandidates from '../../transports/components/TransportAttachCandidates';
import { fetchSupplierOrderTransports } from '../api/orderTransportApi';
import type { SupplierOrderTransportDto } from '../../../dto/transport';
import type { TransportKind } from '../../transports/transportDirection';
import { withTransportReturnContext } from '../../transports/transportReturnContext';
import { canPlanSupplierOrderTransport } from '../supplierOrderStage/canPlanSupplierOrderTransport';

/**
 * Etap 2 (transport przywozowy): "which trucks are bringing this order in",
 * on the supplier order itself.
 *
 * Decision 2026-10-04 (F2): the card also starts the planning — „Zaplanuj
 * transport” opens the transport form with this ZD and the kind preset
 * (`?supplierOrderId=&kind=`), „Dołóż do istniejącego auta” adds the rest of
 * this ZD to a saved factory truck in one click. The list itself stays
 * read-only; links are owned by the transport commands.
 *
 * Kept as its own section component rather than more state on SupplierOrderDetail:
 * the page is already ~2000 lines, and this way the fetch/empty/failure states
 * have a seam that can be tested without mounting the whole order page.
 */
interface SupplierOrderDeliveryTransportsSectionProps {
  supplierOrderId?: string;
  supplierOrderStatus?: string;
  /** Operator-facing ZD number (success message). */
  supplierOrderLabel?: string;
  /** Loading place of this ZD („dojdzie nowy punkt załadunku”). */
  loadingPlace?: string | null;
  /** OPERATIONS / ADMIN: plan and add trucks. */
  canPlanTransports?: boolean;
  canViewCosts?: boolean;
  /** Round 2 (U8): the whole ZD is already planned on trucks. */
  fullyOnTrucks?: boolean;
  onOpenTransport: (transportId: string) => void;
  /**
   * The ZD card reads the same trucks for its stage panel and cost card: the
   * list is fetched once, here; undefined = loading, null = failed.
   */
  onChanged?: () => void;
  onLoaded?: (transports: SupplierOrderTransportDto[] | null | undefined) => void;
  /** The ZD card lists the trucks in its header strip: only actions and freight stay here. */
  hideList?: boolean;
  /** Side-card layout (ZD stage panel): small buttons of equal width, one under another. */
  compact?: boolean;
}

const SupplierOrderDeliveryTransportsSection: React.FC<SupplierOrderDeliveryTransportsSectionProps> = ({
  supplierOrderId,
  supplierOrderStatus,
  supplierOrderLabel = '',
  loadingPlace = null,
  canPlanTransports: permittedToPlan = false,
  canViewCosts = false,
  fullyOnTrucks = false,
  onOpenTransport,
  onLoaded,
  onChanged,
  hideList = false,
  compact = false,
}) => {
  const canPlanTransports = permittedToPlan
    && canPlanSupplierOrderTransport(supplierOrderStatus, { inDeliveryWorkspace: true });
  const { t } = useTranslation(['orders', 'transport']);
  const navigate = useNavigate();
  const [transports, setTransports] = useState<SupplierOrderTransportDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);

  // The newest request wins: a slow answer for another ZD (fast navigation) must not overwrite this one.
  const requestSeq = React.useRef(0);
  const loadTransports = useCallback(async () => {
    if (!supplierOrderId) {
      return;
    }
    requestSeq.current += 1;
    const seq = requestSeq.current;

    setLoading(true);
    onLoaded?.(undefined);
    setLoadFailed(false);
    try {
      const loaded = await fetchSupplierOrderTransports(supplierOrderId);
      if (seq !== requestSeq.current) return;
      setTransports(loaded);
      onLoaded?.(loaded);
    } catch (error) {
      if (seq !== requestSeq.current) return;
      // Advisory only: a failure here degrades this one section, it never
      // blocks the rest of the supplier order.
      setTransports([]);
      setLoadFailed(true);
      onLoaded?.(null);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [supplierOrderId, onLoaded]);

  useEffect(() => {
    void loadTransports();
  }, [loadTransports]);

  const planTransport = (kind: TransportKind) => {
    if (!supplierOrderId) return;
    navigate(withTransportReturnContext(
      `/app/transports/new?supplierOrderId=${encodeURIComponent(supplierOrderId)}&kind=${kind}`,
      { returnTo: `/app/supplier-orders/${encodeURIComponent(supplierOrderId)}` },
    ));
  };

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      {canPlanTransports && supplierOrderId ? (
        <Space wrap={!compact} direction={compact ? 'vertical' : 'horizontal'} size={compact ? 6 : 'small'} style={compact ? { width: '100%' } : undefined} className={compact ? 'zd-aside__actions' : undefined} data-testid="supplier-order-transport-actions">
          <Button
            type="primary"
            size={compact ? 'small' : 'middle'}
            block={compact}
            icon={<ImportOutlined />}
            // Owner 2026-10-10: one start — „Z fabryki”; customers are named per
            // ZD line in „Dla kogo jest ten towar?”, not by picking a kind here.
            onClick={() => planTransport('FACTORY_TO_WAREHOUSE')}
            data-testid="supplier-order-plan-FACTORY"
          >
            {t('transport:plan.planFromFactory')}
          </Button>
          <Tooltip title={fullyOnTrucks ? t('transport:attach.fullyOnTrucksCard') : undefined}>
            <Button
              size={compact ? 'small' : 'middle'}
              block={compact}
              icon={<PlusCircleOutlined />}
              disabled={fullyOnTrucks}
              onClick={() => setAttachOpen(true)}
              data-testid="supplier-order-attach-open"
            >
              {t('transport:attach.open')}
            </Button>
          </Tooltip>
          {fullyOnTrucks && !compact ? (
            <Typography.Text type="secondary" data-testid="supplier-order-attach-disabled-reason">
              {t('transport:attach.fullyOnTrucksCard')}
            </Typography.Text>
          ) : null}
        </Space>
      ) : null}
      {hideList ? null : (
        <DeliveryTransportPicker
          readOnly
          transports={transports}
          value={transports.map((transport) => transport.id)}
          loading={loading}
          loadFailed={loadFailed}
          onRetry={() => { void loadTransports(); }}
          onOpenTransport={onOpenTransport}
          emptyMessage={t('orders:supplierOrders.detail.deliveryTransportsEmpty')}
          data-testid="supplier-order-delivery-transport-list"
        />
      )}
      {/* Usability round 2026-10-05: what each truck costs this ZD (backend figures, transport-cost roles only). */}
      {compact || hideList ? null : <SupplierOrderTruckFreightList transports={transports} onOpenTransport={onOpenTransport} />}
      {supplierOrderId ? (
        <Modal
          open={attachOpen}
          title={t('transport:attach.title')}
          width={760}
          footer={null}
          onCancel={() => setAttachOpen(false)}
          destroyOnHidden
        >
          <TransportAttachCandidates
            target={{ type: 'supplierOrder', id: supplierOrderId, label: supplierOrderLabel, place: loadingPlace }}
            canViewCosts={canViewCosts}
            onAttached={() => {
              setAttachOpen(false);
              void loadTransports();
              onChanged?.();
            }}
          />
        </Modal>
      ) : null}
    </Space>
  );
};

export default SupplierOrderDeliveryTransportsSection;
